import { useState, useEffect, useRef } from "react";
import { ChevronsUpDown, Loader2, Building2, Clock } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandGroup, CommandItem } from "@/components/ui/command";

const RECENT_KEY = "mayr_recent_customers";

export function loadRecentCustomers() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
}
export function pushRecentCustomer(c) {
  if (!c?.id) return;
  const cur = loadRecentCustomers().filter((x) => x.id !== c.id);
  cur.unshift({ id: c.id, title: c.title, subtitle: c.subtitle });
  localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, 6)));
}

export function CustomerCombobox({ value, label, onSelect, testId = "customer-combobox" }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const reqId = useRef(0);

  useEffect(() => { if (open) setRecent(loadRecentCustomers()); }, [open]);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) { setItems([]); setLoading(false); return; }
    setLoading(true);
    const id = ++reqId.current;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: term, types: "customer", limit: 12 } });
        const g = (data.groups || []).find((x) => x.type === "customer");
        if (id === reqId.current) setItems(g ? g.items : []);
      } catch { if (id === reqId.current) setItems([]); }
      finally { if (id === reqId.current) setLoading(false); }
    }, 160);
    return () => clearTimeout(timer);
  }, [q, open]);

  const pick = (it) => { pushRecentCustomer(it); onSelect(it); setOpen(false); setQ(""); };
  const term = q.trim();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} data-testid={testId}
          className={cn("h-9 w-full justify-between gap-2 font-normal", !label && "text-muted-foreground")}>
          <span className="flex min-w-0 items-center gap-2">
            <Building2 className="h-4 w-4 shrink-0 opacity-60" />
            <span className="truncate">{label || t("quotes.customer")}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput autoFocus value={q} onValueChange={setQ}
            placeholder={t("quotes.search_customer")} data-testid={`${testId}-input`} />
          <CommandList>
            {loading && (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("common.loading")}
              </div>
            )}
            {!loading && !term && recent.length > 0 && (
              <CommandGroup heading={t("quotes.recent_customers")}>
                {recent.map((it) => (
                  <CommandItem key={it.id} value={it.id} onSelect={() => pick(it)} data-testid={`${testId}-recent`} className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-slate-400" />
                    <span className="truncate text-sm">{it.title}</span>
                    {it.subtitle && <span className="truncate text-xs text-muted-foreground">· {it.subtitle}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {!loading && !term && recent.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">{t("search.hint")}</div>
            )}
            {!loading && term && items.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">{t("search.empty")}</div>
            )}
            {!loading && items.length > 0 && (
              <CommandGroup>
                {items.map((it) => (
                  <CommandItem key={it.id} value={it.id} onSelect={() => pick(it)} data-testid={`${testId}-option`} className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-slate-400" />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium">{it.title}</span>
                      {it.subtitle && <span className="truncate text-xs text-muted-foreground">{it.subtitle}</span>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
